import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  ArrowLeft,
  Sun,
  Moon,
  User,
  Briefcase,
  BadgeCheck,
  Settings,
  LogOut,
  HelpCircle,
  UserCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

const ReadOnlyField = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
      {label}
    </p>
    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 bg-slate-50 dark:bg-slate-700/50 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700">
      {value || '—'}
    </p>
  </div>
);

const MonProfilPage = () => {
  const { user, logout, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [etablissement, setEtablissement] = useState(user?.etablissement || '');
  const [specialite, setSpecialite] = useState(user?.specialite || '');
  const [saving, setSaving] = useState(false);

  const initials =
    user?.first_name && user?.last_name
      ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
      : 'JD';
  const prefix = user?.profile_type === 'medecin' ? 'Dr.' : 'IDE.';
  const displayName = `${prefix} ${user?.last_name || ''}`;
  const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();

  const handleSave = async () => {
    if (!user?.uid) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), { etablissement, specialite });
      updateUser({ etablissement, specialite });
      toast.success('Profil mis à jour.');
    } catch {
      toast.error('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 transition-colors duration-200">

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-sm transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/pro/infirmier')}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-200 mr-1"
              aria-label="Retour"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </button>
            <img
              src="/RemaH.png"
              alt="Remadia Pro"
              className="w-10 h-10"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="text-xl font-bold text-[#003D82] dark:text-blue-400">Remadia</span>
            <span className="px-2 py-1 bg-teal-500/10 text-teal-600 dark:text-teal-400 text-xs font-semibold rounded-full border border-teal-500/20 dark:border-teal-500/30">
              PRO
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors duration-200"
              aria-label="Changer de thème"
            >
              {isDark ? (
                <Sun className="w-5 h-5 text-yellow-400" />
              ) : (
                <Moon className="w-5 h-5 text-slate-600" />
              )}
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200">
                  <div className="w-8 h-8 rounded-full bg-[#003D82] dark:bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                    {initials}
                  </div>
                  <span className="hidden sm:block text-sm font-medium text-slate-700 dark:text-slate-200">
                    {displayName}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100">
                <DropdownMenuItem onClick={() => navigate('/pro/infirmier/profil')} className="dark:hover:bg-slate-700">
                  <UserCircle className="w-4 h-4 mr-2" />
                  Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/pro/infirmier/compte')} className="dark:hover:bg-slate-700">
                  <Settings className="w-4 h-4 mr-2" />
                  Gestion du compte
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/pro/infirmier/faq')} className="dark:hover:bg-slate-700">
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Aide / FAQ
                </DropdownMenuItem>
                <DropdownMenuSeparator className="dark:bg-slate-700" />
                <DropdownMenuItem
                  onClick={async () => { await logout(); toast.success('Déconnexion réussie'); navigate('/auth/pro'); }}
                  className="text-destructive dark:text-red-400 dark:hover:bg-slate-700"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#003D82] to-[#0056B3] dark:from-slate-800 dark:to-teal-900 text-white py-10 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <UserCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold mb-1">Mon profil</h1>
            <p className="text-blue-100 dark:text-blue-200 text-sm">
              Identité et informations professionnelles
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">

        {/* ── Identité ─────────────────────────────────────── */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 transition-colors duration-200">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-[#003D82]/10 dark:bg-blue-500/20 flex items-center justify-center">
              <User className="w-5 h-5 text-[#003D82] dark:text-blue-400" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Identité</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ReadOnlyField label="Prénom" value={user?.first_name} />
            <ReadOnlyField label="Nom" value={user?.last_name} />
            <ReadOnlyField label="Email" value={user?.email} />
            <ReadOnlyField label="Numéro RPPS" value={user?.rpps} />
          </div>
          <p className="mt-4 text-xs text-slate-400 dark:text-slate-500">
            Ces informations sont verrouillées après vérification. Contactez le support pour toute modification.
          </p>
        </div>

        {/* ── Informations professionnelles ─────────────────── */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 transition-colors duration-200">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-teal-500/10 dark:bg-teal-500/20 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Informations professionnelles</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide block mb-1">
                Établissement
              </label>
              <input
                type="text"
                value={etablissement}
                onChange={(e) => setEtablissement(e.target.value)}
                placeholder="Ex : CHU de Lyon, Clinique Saint-Jean…"
                className="w-full text-sm bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-colors duration-200"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide block mb-1">
                Spécialité / Service
              </label>
              <input
                type="text"
                value={specialite}
                onChange={(e) => setSpecialite(e.target.value)}
                placeholder="Ex : Réanimation, Urgences, Pédiatrie…"
                className="w-full text-sm bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-colors duration-200"
              />
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full py-2.5 bg-teal-500 hover:bg-teal-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors duration-200"
            >
              {saving ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        </div>

        {/* ── Badge professionnel ───────────────────────────── */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 transition-colors duration-200">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 dark:bg-indigo-500/20 flex items-center justify-center">
              <BadgeCheck className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">Badge professionnel</h2>
          </div>

          <div className="bg-gradient-to-br from-[#003D82] to-[#0056B3] rounded-2xl p-6 text-white relative overflow-hidden">
            {/* Décor */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-28 h-28 bg-teal-500/10 rounded-full translate-y-1/2 -translate-x-1/2" />

            <div className="relative flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-white/15 border-2 border-white/30 flex items-center justify-center text-2xl font-bold flex-shrink-0">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-blue-200 uppercase tracking-widest mb-0.5">
                  Infirmier(ère) Diplômé(e) d'État
                </p>
                <p className="text-xl font-bold truncate">{fullName || '—'}</p>
                {(specialite || etablissement) && (
                  <div className="mt-1.5 space-y-0.5">
                    {specialite && (
                      <p className="text-sm text-blue-100 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                        {specialite}
                      </p>
                    )}
                    {etablissement && (
                      <p className="text-sm text-blue-200 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-300 flex-shrink-0" />
                        {etablissement}
                      </p>
                    )}
                  </div>
                )}
                {user?.rpps && (
                  <p className="mt-2 text-xs font-mono text-blue-300">RPPS {user.rpps}</p>
                )}
              </div>
            </div>

            <div className="relative mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
              <span className="text-xs font-semibold text-blue-200 tracking-wider">REMADIA PRO</span>
              <span className="px-2 py-0.5 bg-teal-500/20 border border-teal-400/30 text-teal-300 text-xs font-semibold rounded-full">
                Vérifié
              </span>
            </div>
          </div>

          <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">
            Ce badge est généré à partir de vos informations professionnelles. Complétez votre spécialité et établissement pour l'enrichir.
          </p>
        </div>
      </div>
    </div>
  );
};

export default MonProfilPage;
